/**
 * /admin/memorials/quick — admin fast-path
 * /celebrate/quick-add     — public fast-path (registered users)
 *
 * Auth model:
 *   • Admin route requires super_admin whitelist (POST /api/admin/quick-login).
 *   • Public route requires any signed-in user (goes through the normal
 *     Firebase login), but the memorial is created with status="pending_review"
 *     so admins moderate before publishing.
 *
 * Fields (per user's spec):
 *   • Title       — REQUIRED
 *   • Country     — REQUIRED (structured drop-down)
 *   • Photo       — optional (uploaded to Emergent Object Storage)
 *                    When omitted, backend auto-generates a saffron
 *                    initials portrait so listings always have imagery.
 *   • Description — optional
 *   • State / City — optional
 *   • Date / Time — optional
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, ImagePlus, Loader2, LogIn, ShieldCheck, Wand2, X } from "lucide-react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AiBeautifierModal from "@/components/memorials/AiBeautifierModal";

// Curated country list (small, covers 99% of use). Users can type a
// country not in the list via the "custom" text field that appears at the
// bottom of the dropdown.
const COUNTRIES = [
  "India", "United States", "United Kingdom", "Canada", "Australia",
  "United Arab Emirates", "Singapore", "Malaysia", "New Zealand", "South Africa",
  "Qatar", "Kuwait", "Saudi Arabia", "Bahrain", "Oman",
  "Germany", "France", "Netherlands", "Ireland", "Sweden",
  "Sri Lanka", "Nepal", "Bangladesh", "Pakistan", "Maldives",
  "Kenya", "Nigeria", "Trinidad and Tobago", "Fiji", "Mauritius",
  "Other",
];

// Kerala-first Indian states list (user requested easy Kerala tagging).
const INDIAN_STATES = [
  "Kerala", "Tamil Nadu", "Karnataka", "Andhra Pradesh", "Telangana",
  "Maharashtra", "Gujarat", "Rajasthan", "Punjab", "Haryana",
  "Delhi", "Uttar Pradesh", "Madhya Pradesh", "Bihar", "West Bengal",
  "Odisha", "Assam", "Jharkhand", "Chhattisgarh", "Uttarakhand",
  "Himachal Pradesh", "Goa", "Jammu and Kashmir", "Ladakh",
  "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Sikkim", "Tripura",
  "Arunachal Pradesh", "Andaman and Nicobar", "Chandigarh", "Puducherry",
  "Dadra and Nagar Haveli", "Daman and Diu", "Lakshadweep",
];

const P = {
  saffron:   "#C2410C",
  saffronBg: "#FFF7ED",
  parchment: "#FBE9C6",
  ink:       "#431a04",
  inkSoft:   "#6B4A2E",
  line:      "rgba(194,65,12,0.2)",
  cardBg:    "#FFFFFF",
};

function AdminQuickLogin({ onSuccess }) {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { data } = await http.post("/admin/quick-login", { email: email.trim(), password });
      if (!data?.ok) throw new Error("Login failed");
      toast.success("Signed in as admin.");
      onSuccess && onSuccess();
      // Force a page reload so AuthContext picks up the wofy_session cookie.
      setTimeout(() => window.location.reload(), 250);
    } catch (e2) {
      const msg = e2?.response?.data?.detail || e2.message || "Sign-in failed";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="max-w-md mx-auto px-4 pt-10 pb-16" data-testid="admin-quick-login">
      <div
        className="rounded-2xl p-6"
        style={{ background: P.cardBg, border: `1px solid ${P.line}`, boxShadow: "0 12px 40px -20px rgba(194,65,12,0.25)" }}
      >
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: P.saffron }}>
          Admin sign-in
        </div>
        <h1 className="mt-1 text-2xl leading-tight" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>
          Add a Memorial
        </h1>
        <p className="mt-1 text-xs" style={{ color: P.inkSoft }}>
          Whitelisted admin emails only. Contact the platform team if you need access.
        </p>

        <label className="block mt-5 text-xs font-semibold" style={{ color: P.ink }}>Email</label>
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid="admin-quick-login-email"
          className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
          style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
          placeholder="you@example.com"
          autoComplete="email"
        />

        <label className="block mt-4 text-xs font-semibold" style={{ color: P.ink }}>Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-quick-login-password"
          className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
          style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
          autoComplete="current-password"
        />

        {err && (
          <div
            className="mt-3 text-xs px-3 py-2 rounded-lg"
            style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }}
            data-testid="admin-quick-login-error"
          >
            {err}
          </div>
        )}

        <button
          type="submit"
          disabled={busy}
          data-testid="admin-quick-login-submit"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold"
          style={{ background: P.saffron, color: "#fff" }}
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </div>
    </form>
  );
}

function MemorialForm({ user, mode = "admin" }) {
  const isPublic = mode === "public";
  const [title, setTitle]   = useState("");
  const [country, setCountry] = useState("India");
  const [customCountry, setCustomCountry] = useState("");
  const [state, setState]   = useState("");
  const [city, setCity]     = useState("");
  const [date, setDate]     = useState("");
  const [time, setTime]     = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [notes, setNotes]   = useState("");
  const [description, setDescription] = useState("");
  const [photo, setPhoto]   = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  const [showBeautifier, setShowBeautifier] = useState(false);

  const effectiveCountry = country === "Other" ? customCountry.trim() : country;
  const canSubmit = title.trim() && effectiveCountry && !busy;

  useEffect(() => {
    if (!photo) { setPreview(null); return; }
    const url = URL.createObjectURL(photo);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("country", effectiveCountry);
      if (description.trim()) fd.append("description", description.trim());
      if (state.trim())       fd.append("state", state.trim());
      if (city.trim())        fd.append("city",  city.trim());
      if (date)               fd.append("date",  date);
      if (time)               fd.append("time",  time);
      if (birthDate)          fd.append("birth_date", birthDate);
      if (notes.trim())       fd.append("notes", notes.trim());
      if (photo)              fd.append("photo", photo);
      const endpoint = isPublic ? "/memorials/quick-add" : "/admin/memorials/quick-add";
      const { data } = await http.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(isPublic
        ? "Thank you — your memorial is pending review."
        : "Memorial created — visible now.");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not create memorial. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setTitle(""); setCountry("India"); setCustomCountry("");
    setState(""); setCity(""); setDate(""); setTime(""); setDescription("");
    setBirthDate(""); setNotes("");
    setPhoto(null); setPreview(null); setResult(null);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 pt-8 pb-16" data-testid="admin-quick-add-form">
      <div className="mb-4">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold" style={{ color: P.saffron }}>
          {isPublic
            ? <>Signed in as {user?.email || user?.name || "you"}</>
            : <>Admin · Signed in as {user?.email || user?.name}</>}
        </div>
        <h1 className="mt-1 text-3xl leading-tight" style={{ color: P.ink, fontFamily: "'Playfair Display', Georgia, serif" }}>
          {isPublic ? "Celebrate a Life" : "Add a Memorial"}
        </h1>
        <p className="mt-1 text-sm" style={{ color: P.inkSoft }}>
          Only <b>Title</b> and <b>Country</b> are required. Everything else is optional.
          {isPublic && (
            <> Your memorial will be reviewed by an admin before appearing publicly (usually within a day).</>
          )}
        </p>
      </div>

      {result?.ok && (
        <div
          className="rounded-2xl p-4 mb-6 flex items-center gap-3"
          style={{
            background: result.status === "pending_review" ? "#fff7ed" : "#f0fdf4",
            border: result.status === "pending_review" ? "1px solid rgba(194,65,12,0.25)" : "1px solid #bbf7d0",
          }}
          data-testid="admin-quick-add-success"
        >
          {result.status === "pending_review"
            ? <ShieldCheck className="w-5 h-5" style={{ color: P.saffron }} />
            : <CheckCircle2 className="w-5 h-5" style={{ color: "#15803d" }} />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold" style={{ color: result.status === "pending_review" ? P.ink : "#14532d" }}>
              {result.status === "pending_review"
                ? <>Memorial for “{result.title}” is pending review.</>
                : <>Memorial for “{result.title}” is live.</>}
            </div>
            <div className="text-xs mt-0.5" style={{ color: result.status === "pending_review" ? P.inkSoft : "#166534" }}>
              {result.status === "pending_review"
                ? <>Share the preview link with family: <Link to={result.view_url} className="underline">{result.view_url}</Link></>
                : <Link to={result.view_url} className="underline">View memorial page →</Link>}
            </div>
          </div>
          <button
            onClick={reset}
            className="text-xs font-semibold px-3 py-1.5 rounded-full"
            style={{ background: P.saffron, color: "#fff" }}
            data-testid="admin-quick-add-another"
          >
            Add another
          </button>
          <button
            onClick={() => setShowBeautifier(true)}
            className="text-xs font-semibold px-3 py-1.5 rounded-full inline-flex items-center gap-1"
            style={{ background: "#7A4A1F", color: "#fff" }}
            data-testid="admin-quick-add-beautify"
          >
            <Wand2 className="w-3.5 h-3.5" />
            Beautify with AI
          </button>
        </div>
      )}

      {showBeautifier && result?.slug && (
        <AiBeautifierModal
          slug={result.slug}
          onClose={() => setShowBeautifier(false)}
          canBeautify={true}
          initialMemorial={{
            full_name:    title,
            death_date:   date || "",
            location:     { city, state, country: effectiveCountry },
            short_bio:    description,
          }}
        />
      )}

      <form onSubmit={submit} className="rounded-2xl p-5 space-y-4"
        style={{ background: P.cardBg, border: `1px solid ${P.line}` }}>
        <div>
          <label className="block text-xs font-semibold" style={{ color: P.ink }}>
            Title <span style={{ color: P.saffron }}>*</span>
          </label>
          <input
            type="text" required maxLength={200}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="admin-quick-add-title"
            className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
            style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
            placeholder="e.g. Kamala Devi Nair"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold" style={{ color: P.ink }}>
              Country <span style={{ color: P.saffron }}>*</span>
            </label>
            <select
              required
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="admin-quick-add-country"
              className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none appearance-none"
              style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
            >
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {country === "Other" && (
              <input
                type="text"
                required
                value={customCountry}
                onChange={(e) => setCustomCountry(e.target.value)}
                data-testid="admin-quick-add-country-custom"
                className="mt-2 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
                placeholder="Type country name"
              />
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold" style={{ color: P.ink }}>State / Region</label>
            {effectiveCountry === "India" ? (
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid="admin-quick-add-state"
                className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none appearance-none"
                style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
              >
                <option value="">Select state</option>
                {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            ) : (
              <input
                type="text" maxLength={100}
                value={state}
                onChange={(e) => setState(e.target.value)}
                data-testid="admin-quick-add-state-text"
                className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
                placeholder="Optional"
              />
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold" style={{ color: P.ink }}>City</label>
            <input
              type="text" maxLength={100}
              value={city}
              onChange={(e) => setCity(e.target.value)}
              data-testid="admin-quick-add-city"
              className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
              style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
              placeholder="Optional"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold" style={{ color: P.ink }}>Date of passing</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="admin-quick-add-date"
                className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
              />
            </div>
            <div>
              <label className="block text-xs font-semibold" style={{ color: P.ink }}>Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="admin-quick-add-time"
                className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold" style={{ color: P.ink }}>Date of birth</label>
            <input
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              data-testid="admin-quick-add-birth-date"
              className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
              style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold" style={{ color: P.ink }}>Notes (private)</label>
            <input
              type="text" maxLength={1000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="admin-quick-add-notes"
              className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
              style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
              placeholder="Source, newspaper link, family contact…"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold" style={{ color: P.ink }}>Description</label>
          <textarea
            rows={4} maxLength={4000}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="admin-quick-add-description"
            className="mt-1 w-full rounded-xl px-3 py-2.5 text-sm outline-none"
            style={{ background: P.saffronBg, border: `1px solid ${P.line}`, color: P.ink }}
            placeholder="A short biography, favourite memory, or details for the family."
          />
        </div>

        <div>
          <label className="block text-xs font-semibold mb-1" style={{ color: P.ink }}>Photo</label>
          {preview ? (
            <div
              className="relative w-40 h-40 rounded-2xl overflow-hidden"
              style={{ border: `1px solid ${P.line}` }}
              data-testid="admin-quick-add-photo-preview"
            >
              <img src={preview} alt="Preview" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => setPhoto(null)}
                aria-label="Remove photo"
                data-testid="admin-quick-add-photo-remove"
                className="absolute top-1 right-1 rounded-full p-1"
                style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <label
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold cursor-pointer"
              style={{ background: P.saffronBg, border: `1px dashed ${P.line}`, color: P.saffron }}
              data-testid="admin-quick-add-photo-picker"
            >
              <ImagePlus className="w-4 h-4" />
              Attach photo
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
              />
            </label>
          )}
          <div className="text-[11px] mt-1" style={{ color: P.inkSoft }}>
            Optional. JPG / PNG up to 8 MB.
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="admin-quick-add-submit"
            className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold"
            style={{
              background: canSubmit ? P.saffron : "rgba(194,65,12,0.35)",
              color: "#fff",
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {busy ? "Publishing…" : "Publish Memorial"}
          </button>
        </div>
      </form>

      <p className="mt-4 text-[11px]" style={{ color: P.inkSoft }}>
        {isPublic
          ? "Your submission is queued for admin review. You'll see it appear publicly after approval."
          : "Memorials created here are auto-approved and public. Later, we'll open this workflow to registered users with a light moderation queue."}
      </p>
    </div>
  );
}

export default function AdminMemorialQuickAdd() {
  const { user } = useAuth();
  const isAdmin = !!(user && (user.is_admin || user.platform_role === "super_admin"));

  return (
    <main style={{ background: P.saffronBg, minHeight: "calc(100vh - 200px)" }}>
      {isAdmin
        ? <MemorialForm user={user} mode="admin" />
        : <AdminQuickLogin />}
    </main>
  );
}

/**
 * Public variant — used at /celebrate/quick-add. Any signed-in user can
 * submit. If they aren't signed in yet, we punt to the normal Firebase
 * login flow (which is the app-wide expectation for public interactions).
 */
export function CelebrateQuickAdd() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      // Save intent so post-login we come back here.
      try { sessionStorage.setItem("post_login_redirect", "/celebrate/quick-add"); } catch (_) {}
      navigate("/login?next=/celebrate/quick-add", { replace: true });
    }
  }, [user, navigate]);

  if (!user) return null;

  return (
    <main style={{ background: P.saffronBg, minHeight: "calc(100vh - 200px)" }}>
      <MemorialForm user={user} mode="public" />
    </main>
  );
}
